import React, { useMemo, useState } from 'react';
import { DASH, SYMPTOMS, quarterOptions } from './briefingFormat';

const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
const STATUSES = ['Closed', 'Work In Progress', 'New'];
const PILL_CLASS = { Critical: 'crit', High: 'high', Medium: 'med', Low: 'low' };

const EMPTY = { year: '', symptom: '', period: '', quarter: '', q: '', sev: '', st: '' };

const SEARCH_FIELDS = {
  yr: ['case', 'also', 'subject', 'owner', 'symptomLabel', 'res'],
  az: ['case', 'also', 'subject', 'owner', 'theme', 'symptomLabel', 'res', 'ext']
};

const matches = (row, filters, mode) => {
  if (filters.year && row.year !== filters.year) return false;
  if (filters.symptom && row.symptom !== filters.symptom) return false;
  if (filters.period && row.period !== filters.period) return false;
  if (filters.quarter && row.quarter !== filters.quarter) return false;
  if (filters.sev && row.sev !== filters.sev) return false;
  if (filters.st && row.status !== filters.st) return false;
  const query = filters.q.trim().toLowerCase();
  if (!query) return true;
  return SEARCH_FIELDS[mode].map((field) => row[field] || '').join(' ').toLowerCase().includes(query) || false;
};

const Pill = ({ sev }) => <span className={`pill ${PILL_CLASS[sev] || ''}`}>{sev}</span>;

const countLabel = (rows, filters, cutover) => {
  const bits = [`${rows.length} shown`];
  const symptom = SYMPTOMS.find((item) => item.key === filters.symptom);
  if (symptom) bits.push(symptom.label);
  if (filters.period === 'before') bits.push(`before ${cutover}`);
  if (filters.period === 'after') bits.push(`on or after ${cutover}`);
  if (filters.quarter) bits.push(filters.quarter.toUpperCase());
  return bits.join(' · ');
};

/**
 * One row per issue, with filters. `mode` picks the 2025/2026 or the Azure columns;
 * `initial` pre-fills the symptom and period when a chart segment was clicked.
 */
export default function CaseRegister({ mode, cases, initial, cutover, periods }) {
  const [filters, setFilters] = useState({ ...EMPTY, ...initial });
  const set = (key) => (event) => setFilters((prev) => ({ ...prev, [key]: event.target.value }));
  const rows = useMemo(() => cases.filter((row) => matches(row, filters, mode)), [cases, filters, mode]);
  const isAz = mode === 'az';

  return (
    <section>
      <h2>Case register</h2>
      <p className="note">
        One row per issue. Repeat reports of the same issue are listed under the lead case. Connect2, UAT, and
        internal testing tickets are not listed.
      </p>
      <div className="filters">
        {isAz ? null : (
          <select aria-label="Year" value={filters.year} onChange={set('year')}>
            <option value="">2025 and 2026</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
          </select>
        )}
        <select aria-label="Symptom" value={filters.symptom} onChange={set('symptom')}>
          <option value="">{isAz ? 'All symptoms' : 'All categories'}</option>
          {SYMPTOMS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
        </select>
        {isAz ? (
          <>
            <select aria-label="Period" value={filters.period} onChange={set('period')}>
              <option value="">Before and after {cutover}</option>
              <option value="before">Before {cutover}</option>
              <option value="after">On or after {cutover}</option>
            </select>
            <select aria-label="Quarter" value={filters.quarter} onChange={set('quarter')}>
              <option value="">All quarters</option>
              {quarterOptions(periods).map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </>
        ) : null}
        <input
          type="search"
          aria-label="Search cases"
          placeholder="Search cases"
          size={28}
          value={filters.q}
          onChange={set('q')}
        />
        <select aria-label="Severity" value={filters.sev} onChange={set('sev')}>
          <option value="">All severities</option>
          {SEVERITIES.map((item) => <option key={item}>{item}</option>)}
        </select>
        <select aria-label="Status" value={filters.st} onChange={set('st')}>
          <option value="">All statuses</option>
          {STATUSES.map((item) => <option key={item}>{item}</option>)}
        </select>
        <button type="button" className="clear-btn" onClick={() => setFilters(EMPTY)}>Clear</button>
        <span className="note" role="status">{countLabel(rows, filters, cutover)}</span>
      </div>
      <div className="scroll">
        <table className="reg">
          <thead>
            <tr>
              {isAz ? null : <th>Year</th>}
              <th>Opened</th><th>Case</th><th>Status</th><th>Sev.</th><th>Age</th>
              <th>{isAz ? 'Symptom' : 'Category'}</th><th>Subject</th><th>Owner</th>
              {isAz ? <th>External</th> : null}
              <th>Resolution comment</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr className={row.status === 'Closed' ? undefined : 'open'} key={`${row.case}-${index}`}>
                {isAz ? null : <td>{row.year}</td>}
                <td>{row.opened}</td>
                <td>
                  {row.case}
                  {row.also ? <div className="note">Same issue also filed as {row.also}</div> : null}
                </td>
                <td>{row.status}</td>
                <td><Pill sev={row.sev} /></td>
                <td>{row.ageLabel}</td>
                <td>{row.symptomLabel}</td>
                <td>{row.subject}</td>
                <td>{row.owner}</td>
                {isAz ? <td>{row.ext || ''}</td> : null}
                <td>{row.res || DASH}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
